import { useId, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

type FieldProps = {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  optional?: boolean;
  children: (control: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) => ReactNode;
  className?: string;
};

/** Label, control, hint and error, wired together for assistive tech. */
export function Field({ label, hint, error, optional, children, className }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const describedBy = [hint && hintId, error && errId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={['ui-field', className].filter(Boolean).join(' ')}>
      <label className="ui-label" htmlFor={id}>
        {label}
        {optional && <span className="ui-label__optional">optional</span>}
      </label>
      {children({ id, ...(describedBy && { 'aria-describedby': describedBy }), ...(error && { 'aria-invalid': true }) })}
      {hint && !error && (
        <p className="ui-hint" id={hintId}>
          {hint}
        </p>
      )}
      {error && (
        <p className="ui-error-text" id={errId}>
          <AlertCircle size={14} aria-hidden />
          {error}
        </p>
      )}
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode }[];
  label: string;
}) {
  return (
    <div className="ui-segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Choices<T extends string>({
  legend,
  name,
  value,
  onChange,
  options,
  columns,
}: {
  legend: ReactNode;
  name: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode; help?: ReactNode; disabled?: boolean }[];
  columns?: number;
}) {
  return (
    <fieldset className="ui-choices">
      <legend className="ui-label">{legend}</legend>
      <div className="ui-choices__grid" style={columns ? { gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${Math.floor(560 / columns)}px), 1fr))` } : undefined}>
        {options.map((o) => (
          <label key={o.value} className="ui-choice">
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={value === o.value}
              disabled={o.disabled}
              onChange={() => onChange(o.value)}
            />
            <span className="ui-choice__title">
              {o.label}
              <span className="ui-choice__radio" aria-hidden />
            </span>
            {o.help && <span className="ui-choice__help">{o.help}</span>}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
